import { Divider } from "antd";
import { Form } from "antd-v5";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { isEmpty } from "lodash";
import React, { forwardRef, useCallback, useMemo, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, urlRegexValidator } from "utils/formUtils";

import GenericPullCredentialsForm from "../GenericPullCredentialsForm";
import AxoniusMappingForm from "./AxoniusMappingForm";

function AxoniusCredentialsForm(props, ref) {
  const featureFlags = useFeatureFlag();
  const [form] = Form.useForm();
  const [fieldValues, setFieldValues] = useState(() => {
    if (props.config) {
      const { apiEndpoint, apiKey, apiSecret, fetchAssets } = props.config;
      return {
        apiEndpoint: apiEndpoint,
        apiKey,
        apiSecret,
        fetchAssets: fetchAssets === "true" || fetchAssets === true,
      };
    }
    return {
      apiEndpoint: undefined,
      clientId: undefined,
      clientSecret: undefined,
      fetchAssets: false,
    };
  });

  const fetchProjects = useCallback(
    (params) => {
      const { apiSecret, apiEndpoint, apiKey } = fieldValues;

      if (apiEndpoint && apiKey && apiSecret) {
        const config = {
          apiEndpoint,
          apiKey,
          apiSecret,
        };
        let { promise, cancel } = ToolsIntegrationsAPI.getProjectByToolName(
          {
            config,
            ...params,
          },
          props.name,
        );

        promise = promise.then((response) => {
          const { projects } = response.data;
          return projects;
        });
        return { promise, cancel };
      }
    },
    [fieldValues.apiEndpoint, fieldValues.apiKey, fieldValues.apiSecret],
  );

  const fields = useMemo(() => {
    const { apiSecret, apiEndpoint, apiKey, fetchAssets } = fieldValues;
    return [
      {
        name: "apiEndpoint",
        label: "API Endpoint",
        required: true,
        rules: [urlRegexValidator],
        props: {
          onChange: (e) =>
            setFieldValues((prev) => ({
              ...prev,
              apiEndpoint: e.target.value,
            })),
        },
      },
      {
        name: "apiKey",
        label: "API Key",
        required: true,
        props: {
          onChange: (e) =>
            setFieldValues((prev) => ({ ...prev, apiKey: e.target.value })),
        },
      },
      {
        name: "apiSecret",
        label: "API Secret",
        type: FieldTypes.PASSWORD,
        required: true,
        props: {
          onChange: (e) =>
            setFieldValues((prev) => ({ ...prev, apiSecret: e.target.value })),
        },
      },
      ...(featureFlags.RBVM_ASSETS
        ? [
            {
              render: () => <Divider type="horizontal" className="m-y-m" />,
            },
            {
              name: "fetchAssets",
              label: "Fetch Assets",
              type: FieldTypes.BOOLEAN,
              disabled: !apiSecret,
              inline: true,
              props: {
                checked: !!fetchAssets,
                onChange: (e) =>
                  setFieldValues((prev) => ({
                    ...prev,
                    fetchAssets: e?.target?.checked,
                  })),
              },
            },
            {
              name: "adapterName",
              label: "Adapter Name",
              placeholder: "Leave blank to fetch assets from all adapters",
              help: "If adapter is selected, we will display the 'Adapter specific' asset details. Otherwise, we will display the 'Aggregated Fields' details from Axonius.",
              type: FieldTypes.MULTI_SELECT,
              disabled: !(apiEndpoint && apiSecret && apiKey && fetchAssets),
              props: {
                api:
                  apiEndpoint && apiKey && apiSecret
                    ? fetchProjects
                    : undefined,
                allowClear: true,
              },
            },
            {
              name: "search",
              label: "Search Query",
              NoDefaultLengthRule: true,
              disabled: !(apiEndpoint && apiSecret && apiKey && fetchAssets),
              rules: [
                { max: 512, message: "Maximum length is 512 characters" },
              ],
              help: (
                <>
                  Query Created with the Query Wizard{" "}
                  <a
                    href="https://docs.axonius.com/docs/query-wizard-and-query-filter"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Creating Queries with the Query Wizard | Axonius
                  </a>
                </>
              ),
            },
          ]
        : []),
    ];
  }, [
    featureFlags.RBVM_ASSETS,
    fieldValues.apiKey,
    fieldValues.apiEndpoint,
    fieldValues.apiSecret,
    fieldValues.fetchAssets,
    fetchProjects,
  ]);

  const parseExtraFields = useCallback((config) => {
    return {
      ...config,
      adapterName: !isEmpty(config.adapterName)
        ? config.adapterName.split(",")
        : undefined,
      fetchAssets: config.fetchAssets === "true",
    };
  }, []);

  const beforeSave = useCallback((data) => {
    const { adapterName } = data;
    return {
      ...data,
      adapterName: !isEmpty(adapterName) ? adapterName.join() : undefined,
    };
  }, []);

  return (
    <GenericPullCredentialsForm
      {...props}
      ref={ref}
      form={form}
      fields={fields}
      MappingForm={AxoniusMappingForm}
      parseExtraFields={parseExtraFields}
      beforeSave={beforeSave}
    />
  );
}

export default forwardRef(AxoniusCredentialsForm);
